import { VariationsMetricsByWeekRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { VariationsFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { calculatePercentChange } from 'src/utils/app';
import { TimeSeriesData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/classes/TimeSeriesData';
import { ComparisonPeriod, WeekRange } from 'src/utils/Hooks';

export interface VariationsSplineChartData {
  chartData: {
    primaryMetrics: [number, number][];
    secondaryMetrics: [number, number][];
  };
  legendData: {
    primaryLegendMetric: number;
    secondaryLegendMetric: number;
    percentChange: number;
  };
}

export class VariationsSplineAdapter implements ChartDataAdapter<VariationsSplineChartData> {
  private primaryData: TimeSeriesData;
  private comparisonData: TimeSeriesData;
  private variationsFilter: VariationsFilter;
  private comparisonPeriod: string;
  private weekRange: string;

  constructor(
    primaryRecords: VariationsMetricsByWeekRecord[],
    comparisonRecords: VariationsMetricsByWeekRecord[],
    variationsFilter: VariationsFilter,
    comparisonPeriod: string = ComparisonPeriod.PRIOR_YEAR,
    weekRange: string = WeekRange.YTD,
    mainTimePeriod?: { startWeek: number; endWeek: number },
    comparisonTimePeriod?: { startWeek: number; endWeek: number }
  ) {
    this.variationsFilter = variationsFilter;
    this.comparisonPeriod = comparisonPeriod;
    this.weekRange = weekRange;

    this.primaryData = new TimeSeriesData([]).createWeekRecords(primaryRecords, (record) =>
      this.getMetricValue(record)
    );
    this.comparisonData = new TimeSeriesData([]).createWeekRecords(comparisonRecords, (record) =>
      this.getMetricValue(record)
    );

    if (mainTimePeriod) {
      this.primaryData.fillMissingWeeksInRange(mainTimePeriod.startWeek, mainTimePeriod.endWeek);
    }
    if (comparisonTimePeriod) {
      this.comparisonData.fillMissingWeeksInRange(comparisonTimePeriod.startWeek, comparisonTimePeriod.endWeek);
    }
  }

  private getMetricValue(record: VariationsMetricsByWeekRecord): number {
    switch (this.variationsFilter) {
      case VariationsFilter.ChildVariationRate:
        return record.childVariationRate;
      case VariationsFilter.SingleSkuPages:
        return record.singleSkuCount;
      case VariationsFilter.VariatedSkuPages:
        return record.variatedSkuCount;
      case VariationsFilter.ChildAccuracy:
        return record.childAccuracy;
      case VariationsFilter.CorrectChildSkus:
        return record.correctChildCount;
      case VariationsFilter.IncorrectChildSkus:
        return record.incorrectChildCount;
      case VariationsFilter.ParentPageAccuracy:
        return record.parentPageAccuracy;
      case VariationsFilter.InaccurateParentPages:
        return record.inaccurateParentCount;
      case VariationsFilter.PageDensity:
        return Math.min(record.pageDensity, 1.0);
      case VariationsFilter.ParentPageCount:
        return record.parentPageCount;
      default:
        return 0;
    }
  }

  transform(): VariationsSplineChartData {
    const primaryData = this.primaryData.clone();
    const comparisonData = this.comparisonData.clone();

    // No year incrementing needed - SplineChartV2 uses category-based (index-based) positioning
    // Prior period merging is handled by SplineChartV2's useSplineChartV2Data hook

    // Use toWeekIdSeries() for SplineChartV2 which uses category-based x-axis with weekIds
    const primaryMetrics = primaryData.toWeekIdSeries();
    const secondaryMetrics = comparisonData.toWeekIdSeries();

    let primaryLegendMetric: number;
    let secondaryLegendMetric: number;

    if (this.weekRange === WeekRange.LAST_WEEK) {
      const lastWeekOfPrimaryData = primaryData.getLastElement();
      primaryLegendMetric = lastWeekOfPrimaryData ? lastWeekOfPrimaryData[1] : 0;

      if (this.comparisonPeriod === ComparisonPeriod.PRIOR_YEAR) {
        // Prior Year comparison: Finds the matching week number in comparison data
        // Match by week number only (weekId % 100) since comparison data has prior year weekIds
        const lastPrimaryWeekNumber = lastWeekOfPrimaryData ? lastWeekOfPrimaryData[0] % 100 : 0;
        const comparisonPoints = comparisonData.getPoints();
        const matchingComparisonPoint = comparisonPoints.find(
          ([weekId]) => weekId % 100 === lastPrimaryWeekNumber
        );
        secondaryLegendMetric = matchingComparisonPoint ? matchingComparisonPoint[1] : 0;
      } else {
        // Prior Period comparison: Uses the last comparison value
        const lastWeekOfComparisonData = comparisonData.getLastElement();
        secondaryLegendMetric = lastWeekOfComparisonData ? lastWeekOfComparisonData[1] : 0;
      }
    } else {
      // For other longer periods, compute averages
      primaryLegendMetric = this.primaryData.compute('average');
      secondaryLegendMetric = this.comparisonData.compute('average');
    }

    let percentChange = 0;
    if (this.comparisonData.isValid() && secondaryLegendMetric !== 0) {
      percentChange = calculatePercentChange(primaryLegendMetric, secondaryLegendMetric);
    }

    return {
      chartData: {
        primaryMetrics,
        secondaryMetrics
      },
      legendData: {
        primaryLegendMetric,
        secondaryLegendMetric,
        percentChange
      }
    };
  }
}
